<template>
  <div 
    class="graph-wrapper" 
    :class="{'active-graph': metric === currentMetric}">
    <div class="previos-btn-wrapper">
      <button class="btn btn-transparent-bkg" @click.prevent="changeSelectedMetric(prevMetric)">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
    </div>
    <div class="next-btn-wrapper">
      <button class="btn btn-transparent-bkg" @click.prevent="changeSelectedMetric(nextMetric)">
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <DonutChart 
      :metric="metric"
      :chartData="generatedChartData"
      :graphOptions="graphOptions"
      :currentMetric="currentMetric" />

    <MetricInfo 
      :session-metrics="sessionMetrics"
      :metric="metric" 
      :value="chartValue"
      :currentMetric="currentMetric"
      :changeSelectedMetric="changeSelectedMetric"
      currentPage="activity" />
  </div>
</template>

<script>
import { SESSION_METRICS } from '@/constants/SessionConsts'
import DonutChart from '@/components/common/chart/DonutChart'
import MetricInfo from '@/components/calendar-activity/MetricInfo'

export default {
  name: 'CalendarActivityTab',
  props: [
    'metric', 
    'chartValue',
    'currentMetric',
    'changeSelectedMetric'
  ],
  data () {
    return {
      hrFullCircleValue: 45,
      apFullCircleValue: 60,
      distanceFullCircleValue: 7500,
      workloadFullCircleValue: 30,
      sessionMetrics: SESSION_METRICS
    }
  },
  computed: {
    nextMetric () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          return this.sessionMetrics.AP
        case this.sessionMetrics.AP:
          return this.sessionMetrics.WORKLOAD
        case this.sessionMetrics.WORKLOAD:
          return this.sessionMetrics.DISTANCE
        case this.sessionMetrics.DISTANCE:
          return this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR
      }
    },
    prevMetric () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          return this.sessionMetrics.DISTANCE
        case this.sessionMetrics.DISTANCE:
          return this.sessionMetrics.WORKLOAD
        case this.sessionMetrics.WORKLOAD:
          return this.sessionMetrics.AP
        case this.sessionMetrics.AP:
          return this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR
      }
    },
    generatedChartData () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          const hrValueThirdPart = this.hrFullCircleValue / 3

          return [
            ['HR', 'Zone'],
            ['Light',         (this.chartValue > hrValueThirdPart ? hrValueThirdPart: this.chartValue)],
            ['Moderate',      (this.chartValue < hrValueThirdPart ? 0 : (this.chartValue < (hrValueThirdPart * 2) ? (this.chartValue - hrValueThirdPart) : hrValueThirdPart)) ],
            ['Vigorous',      this.chartValue < (hrValueThirdPart * 2) ? 0 : (this.chartValue < this.hrFullCircleValue ? (this.chartValue - (hrValueThirdPart * 2)) : hrValueThirdPart)],
            ['Transparent',   (this.hrFullCircleValue - this.chartValue < 0 ? 0 : this.hrFullCircleValue - this.chartValue) ]
          ]

          break;
        case this.sessionMetrics.AP:
          const apValueThirdPart = this.apFullCircleValue / 3
          const apValue = this.chartValue < 20 ? 0 : (this.chartValue - 20)

          return [
            ['AP', 'Zone'],
            ['Light',         (apValue > apValueThirdPart ? apValueThirdPart : apValue)],
            ['Moderate',      (apValue < apValueThirdPart ? 0 : (apValue < (apValueThirdPart * 2) ? (apValue - apValueThirdPart) : apValueThirdPart)) ],
            ['Vigorous',      apValue < (apValueThirdPart * 2) ? 0 : (apValue < this.apFullCircleValue ? (apValue - (apValueThirdPart * 2)) : apValueThirdPart)],
            ['Transparent',   (this.apFullCircleValue - apValue < 0 ? 0 : this.apFullCircleValue - apValue) ]
          ]

          break;
        case this.sessionMetrics.DISTANCE:
          const distanceThirdPart = this.distanceFullCircleValue / 3

          return [
            ['Distance', 'Zone'],
            ['Light',          (this.chartValue > distanceThirdPart ? (this.distanceFullCircleValue /3) : this.chartValue)],
            ['Moderate',       (this.chartValue < distanceThirdPart ? 0 : (this.chartValue < (distanceThirdPart * 2) ? (this.chartValue - distanceThirdPart) : distanceThirdPart)) ],
            ['Vigorous',       (this.chartValue < (distanceThirdPart * 2) ? 0 : (this.chartValue < this.distanceFullCircleValue ? (this.chartValue - (distanceThirdPart * 2)) : distanceThirdPart))],
            ['Transparent',    (this.distanceFullCircleValue - this.chartValue < 0 ? 0 : this.distanceFullCircleValue - this.chartValue) ]
          ]

          break;
        case this.sessionMetrics.WORKLOAD:
          const workloadThirdPart = this.workloadFullCircleValue / 3

          return [
            ['Speed', 'Zone'],
            ['Light',          (this.chartValue > workloadThirdPart ? workloadThirdPart : this.chartValue)],
            ['Moderate',       (this.chartValue < workloadThirdPart ? 0 : (this.chartValue < (workloadThirdPart * 2) ? (this.chartValue - workloadThirdPart) : workloadThirdPart)) ],
            ['Vigorous',       (this.chartValue < (workloadThirdPart * 2) ? 0 : (this.chartValue < this.workloadFullCircleValue ? (this.chartValue - (workloadThirdPart * 2)) : workloadThirdPart))],
            ['Transparent',    (this.workloadFullCircleValue - this.chartValue < 0 ? 0 : this.workloadFullCircleValue - this.chartValue) ]
          ]

          break;
      }
    },
    graphOptions () {
      let colors = []

      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          colors = [
            '#E17070',
            '#D04A4A',
            '#CA3131',
            '#1A1A1D'
          ]
          break
        case this.sessionMetrics.AP:
          colors = [
            '#CAAD6B',
            '#BA9647',
            '#B08625',
            '#1A1A1D'
          ]
          break
        case this.sessionMetrics.DISTANCE:
          colors = [
            '#92B4C0',
            '#699CAE',
            '#397F95',
            '#1A1A1D'
          ]
          break
        case this.sessionMetrics.WORKLOAD:
          colors = [
            '#AB64EF',
            '#8D1DEF',
            '#521A91',
            '#1A1A1D'
          ]
          break
      }

      return {
        backgroundColor: {
          fill: 'transparent',
        },
        animation:{
          duration: 1000,
          easing: 'out',
        },
        pieSliceText: 'none',
        pieSliceBorderColor: '#1A1A1D',
        legend: 'none',
        tooltip: { trigger: 'none' },
        pieHole: 0.9,
        colors,
        chartArea: {
          left: 20,
          right: 20,
          top: 20,
          bottom: 20,
          width: '100%',
          height: '100%'
        },
        slices: {
          0: { color: colors[0] },
          1: { color: colors[1] },
          2: { color: colors[2] },
          3: { color: colors[3] }
        }
      };
    }
  },
  components: {
    DonutChart,
    MetricInfo
  }
}
</script>

<style lang="scss" scoped>
 .graph-wrapper {
    position: relative;
    min-width: 270px;

    .previos-btn-wrapper, .next-btn-wrapper {
      display: none;
      position: absolute;
      height: 30px;
      top: 0;
      bottom: 0;
      margin: auto;
      background: transparent;
    }

    .previos-btn-wrapper {
      left: 0;
    }

    .next-btn-wrapper {
      right: 0;
    }

    .chart {
      min-height: 320px;
    }

    .metric-statistics {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;

      .metric-cell-wrapper {
        display: table;
        width: 100%;
        height: 100%;

        .metric-cell {
          display: table-cell;
          vertical-align: middle;
          text-align: center;
        }
      }
    }

    .info-btn-wrapper {
      position: absolute;
      right: 20px;
      top: 0;

      .static {
        display: inline-block;
      }

      .hover {
        display: none;
      }

      &:hover {
        .static {
          display: none;
        }
  
        .hover {
          display: inline-block;
        }
      }
    }
  }
</style>
